import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Facebook, Instagram, Twitter, Linkedin, Youtube, Mail, ArrowRight } from "lucide-react";

const LOGO_URL = "/logo.png";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-tbc-navy text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <img src="/logo-white.png" alt="The Blk Community" className="h-12 w-auto" />
            </Link>
            <p className="text-gray-400 font-body mb-6 max-w-sm">
              The Blk Community is a marketplace connecting you with authentic Black-owned businesses. 
              Shop, support, and celebrate Black excellence.
            </p>
            <p className="font-heading text-tbc-gold text-sm italic mb-6">
              Find what you love. Support who made it.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-tbc-gold hover:text-tbc-navy transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-tbc-gold hover:text-tbc-navy transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-tbc-gold hover:text-tbc-navy transition-colors"
                data-testid="social-twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-tbc-gold hover:text-tbc-navy transition-colors"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-tbc-gold hover:text-tbc-navy transition-colors"
                data-testid="social-youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg mb-4 text-tbc-gold">Explore</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white transition-colors font-body text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/directory" className="text-gray-400 hover:text-white transition-colors font-body text-sm">
                  Business Directory
                </Link>
              </li>
              <li>
                <Link to="/blinterest" className="text-gray-400 hover:text-white transition-colors font-body text-sm">
                  Blinterest
                </Link>
              </li>
              <li>
                <Link to="/news" className="text-gray-400 hover:text-white transition-colors font-body text-sm">
                  News & Stories
                </Link>
              </li>
            </ul>
          </div>

          {/* For Business */}
          <div>
            <h4 className="font-heading text-lg mb-4 text-tbc-gold">For Business</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors font-body text-sm">
                  List Your Business
                </Link>
              </li>
              <li>
                <Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors font-body text-sm">
                  Seller Dashboard
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors font-body text-sm">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/founding-100" className="text-gray-400 hover:text-white transition-colors font-body text-sm" data-testid="footer-founding-100-link">
                  The Founding 100
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading text-lg mb-4 text-tbc-gold">Stay Connected</h4>
            <p className="text-gray-400 font-body text-sm mb-4">
              Subscribe for updates, events, and community news.
            </p>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Your email"
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 rounded-full h-10 text-sm"
                data-testid="footer-newsletter-email"
              />
              <Button
                size="icon"
                className="bg-tbc-coral hover:bg-tbc-coral/90 text-white rounded-full h-10 w-10 flex-shrink-0"
                data-testid="footer-newsletter-submit"
              >
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-400 font-body text-sm text-center md:text-left">
              <p>© {currentYear} The Blk Community. All rights reserved.</p>
              <p className="text-xs text-gray-500 mt-1" data-testid="footer-parent-company">
                A <span className="text-tbc-gold">Midas Touch Group LLC</span> company.
              </p>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors font-body" data-testid="footer-privacy-link">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors font-body" data-testid="footer-terms-link">
                Terms of Service
              </Link>
              <Link to="/contact" className="text-gray-400 hover:text-white transition-colors font-body" data-testid="footer-contact-link">
                Contact Us
              </Link>
              <button
                type="button"
                onClick={() => {
                  try {
                    localStorage.removeItem("tbc.cookie_consent");
                    toast.success("Cookie preferences reset. Refresh the page to choose again.");
                  } catch {
                    toast.error("Couldn't reset — your browser blocks storage.");
                  }
                }}
                className="text-gray-400 hover:text-white transition-colors font-body cursor-pointer"
                data-testid="footer-manage-cookies-btn"
                title="Clear your saved cookie choice so the banner reappears on next page load"
              >
                Manage Cookies
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
